import { useSelector } from "@tanstack/react-store"
import * as m from "@/paraglide/messages"
import type { Status, SuspendReason } from "./connection"
import { wsStore } from "./store"

type Banner = "none" | "reconnecting" | "error" | "superseded"

const ERROR_AFTER_ATTEMPTS = 5

function resolveBanner(
	status: Status,
	suspendReason: SuspendReason | null,
	reconnectAttempts: number,
): Banner {
	if (status === "suspended") {
		if (suspendReason === "offline") return "error"
		if (suspendReason === "superseded") return "superseded"
		return "none"
	}
	const isRetrying =
		status === "backoff" || (status === "connecting" && reconnectAttempts > 0)
	if (!isRetrying) return "none"
	return reconnectAttempts >= ERROR_AFTER_ATTEMPTS ? "error" : "reconnecting"
}

export function WsConnectionStatus() {
	const status = useSelector(wsStore, (s) => s.status)
	const suspendReason = useSelector(wsStore, (s) => s.suspendReason)
	const reconnectAttempts = useSelector(wsStore, (s) => s.reconnectAttempts)

	const banner = resolveBanner(status, suspendReason, reconnectAttempts)

	if (banner === "none") {
		return null
	}

	if (banner === "error") {
		return (
			<div className="keyboard-slide-up fixed inset-x-0 bottom-0 z-50 bg-red-500 py-2 text-center text-white">
				{m.connectionError()}
			</div>
		)
	}

	return (
		<div className="keyboard-slide-up fixed inset-x-0 bottom-0 z-50 bg-white py-2 text-center text-black">
			{banner === "superseded"
				? m.connectionSuperseded()
				: m.connectionReconnecting()}
		</div>
	)
}
